import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, 
  Dog, 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  Download
} from 'lucide-react';

interface OccupancyData {
  date: string;
  occupied: number;
  capacity: number;
  rate: number;
}

interface ReportStats {
  averageOccupancy: number;
  peakOccupancy: number;
  lowestOccupancy: number;
  totalRevenue: number;
  totalDays: number;
}

const DolulukRaporu: React.FC = () => {
  const [occupancyData, setOccupancyData] = useState<OccupancyData[]>([]);
  const [stats, setStats] = useState<ReportStats>({
    averageOccupancy: 0,
    peakOccupancy: 0,
    lowestOccupancy: 0,
    totalRevenue: 0,
    totalDays: 0
  });
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const generateDemoData = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const data: OccupancyData[] = [];
    const capacity = 50;

    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      // Demo doluluk oranı - hafta sonları daha yüksek
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const baseOccupancy = isWeekend ? 35 : 25;
      const randomVariation = Math.floor(Math.random() * 15);
      const occupied = Math.min(capacity, baseOccupancy + randomVariation);
      
      data.push({
        date: date.toISOString().split('T')[0],
        occupied,
        capacity,
        rate: Math.round((occupied / capacity) * 100)
      });
    }

    return data;
  };

  const fetchOccupancyData = useCallback(async () => {
    setLoading(true);
    try {
      // Demo veri oluştur
      const data = generateDemoData(startDate, endDate);
      setOccupancyData(data);

      // İstatistikleri hesapla
      const rates = data.map(d => d.rate);
      const revenues = data.map(d => d.occupied * 100); // Günlük 100 TL farz edelim
      
      setStats({
        averageOccupancy: Math.round(rates.reduce((sum, rate) => sum + rate, 0) / rates.length),
        peakOccupancy: Math.max(...rates),
        lowestOccupancy: Math.min(...rates),
        totalRevenue: revenues.reduce((sum, rev) => sum + rev, 0),
        totalDays: data.length
      });

    } catch (error) {
      // Fetch occupancy data error
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchOccupancyData();
  }, [fetchOccupancyData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getOccupancyColor = (rate: number) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-yellow-500';
    if (rate >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTextColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    if (rate >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doluluk Raporu</h1>
          <p className="text-gray-600">Otel doluluk oranları ve analizi</p>
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <button
            onClick={fetchOccupancyData}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
          >
            <RefreshCw size={20} className="mr-2" />
            Yenile
          </button>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <Download size={20} className="mr-2" />
            Excel İndir
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{stats.totalDays}</span> gün analiz ediliyor
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ortalama Doluluk</p>
              <p className="text-2xl font-bold text-gray-900">%{stats.averageOccupancy}</p>
            </div>
            <div className="text-blue-500">
              <BarChart3 size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Yüksek Doluluk</p>
              <p className="text-2xl font-bold text-green-600">%{stats.peakOccupancy}</p>
            </div>
            <div className="text-green-500">
              <TrendingUp size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En Düşük Doluluk</p>
              <p className="text-2xl font-bold text-red-600">%{stats.lowestOccupancy}</p>
            </div>
            <div className="text-red-500">
              <TrendingDown size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Toplam Gelir</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
            </div>
            <div className="text-purple-500">
              <Dog size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Günlük Doluluk Grafiği</h3>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
              <span>%80+ (Mükemmel)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
              <span>%60-79 (İyi)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
              <span>%40-59 (Orta)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
              <span>%0-39 (Düşük)</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {occupancyData.map((day, index) => (
              <div key={day.date} className="flex items-center space-x-4">
                <div className="w-16 text-sm text-gray-600">
                  {formatDate(day.date)}
                </div>
                <div className="flex-1 relative">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className={`h-6 rounded-full ${getOccupancyColor(day.rate)} transition-all duration-300`}
                      style={{ width: `${day.rate}%` }}
                    ></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white">
                    {day.occupied}/{day.capacity}
                  </div>
                </div>
                <div className={`w-12 text-sm font-medium ${getTextColor(day.rate)}`}>
                  %{day.rate}
                </div>
                <div className="w-20 text-sm text-gray-600">
                  {formatCurrency(day.occupied * 100)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Detaylı Analiz</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dolu Oda
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Toplam Kapasite
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doluluk Oranı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Günlük Gelir
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {occupancyData.slice(-10).reverse().map((day) => (
                <tr key={day.date} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {new Date(day.date).toLocaleDateString('tr-TR', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {day.occupied}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {day.capacity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getTextColor(day.rate)}`}>
                      %{day.rate}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(day.occupied * 100)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      day.rate >= 80 ? 'bg-green-100 text-green-800' :
                      day.rate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      day.rate >= 40 ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {day.rate >= 80 ? 'Mükemmel' :
                       day.rate >= 60 ? 'İyi' :
                       day.rate >= 40 ? 'Orta' : 'Düşük'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DolulukRaporu;
